import { FC, Dispatch, useEffect, useState } from "react";
import { PdfHandler } from "../../../middleware/handlers/pdf-handler";
import {
  FormatPdfMeasurement,
  ParsePdfLength,
  PDF_FULL_SIZE_SCALE,
  PDF_SCALE_PRESETS,
  type PdfCalibration,
  type PdfMeasurement,
  type PdfMeasureTool,
  type PdfMeasureUnits,
  type PdfScale,
} from "../../../core/pdf/pdf-measure";

interface PdfMeasureMenuProps {
  measurePanelReveal: boolean;
  measureTool: PdfMeasureTool;
  setMeasureTool: Dispatch<PdfMeasureTool>;
  measureUnits: PdfMeasureUnits;
  setMeasureUnits: Dispatch<PdfMeasureUnits>;
  pageNumber: number;
  measurements: PdfMeasurement[];
  pageScales: { [pageNumber: number]: PdfScale };
  calibration: PdfCalibration | null;
}

// how each measurement is drawn; the viewer listens for the clicks named
const measureTools: { tool: PdfMeasureTool; label: string; hint: string }[] = [
  {
    tool: "LENGTH",
    label: "Length",
    hint: "Click two points.",
  },
  {
    tool: "AREA",
    label: "Area",
    hint: "Click each corner, then double-click or press Enter.",
  },
  {
    tool: "PERIMETER",
    label: "Perimeter",
    hint: "Click each corner, then double-click or press Enter.",
  },
  {
    tool: "CALIBRATE",
    label: "Set scale from a known length",
    hint: "Click both ends of a dimension you know, then type its length.",
  },
];

const measureUnitOptions: { units: PdfMeasureUnits; label: string }[] = [
  { units: "IMPERIAL", label: "Imperial" },
  { units: "METRIC", label: "Metric" },
];

const measurementNames = {
  LENGTH: "Length",
  AREA: "Area",
  PERIMETER: "Perimeter",
};

const presetScales = PDF_SCALE_PRESETS.flatMap(({ scales }) => scales);

export const PdfMeasureMenu: FC<PdfMeasureMenuProps> = ({
  measurePanelReveal,
  measureTool,
  setMeasureTool,
  measureUnits,
  setMeasureUnits,
  pageNumber,
  measurements,
  pageScales,
  calibration,
}) => {
  // the real length being typed for the two points just clicked
  const [knownLength, setKnownLength] = useState("");

  useEffect(() => {
    setKnownLength("");
  }, [calibration]);

  if (!measurePanelReveal) return null;

  const pageScale = pageScales[pageNumber] ?? PDF_FULL_SIZE_SCALE;
  const knownLengthMetres = ParsePdfLength(knownLength, measureUnits);

  const ApplyKnownLength = () => {
    if (knownLengthMetres !== null) {
      PdfHandler.ApplyCalibration(knownLengthMetres);
      setMeasureTool("LENGTH");
    }
  };

  return (
    <div className="viewerToolPanel">
      <div className="viewerToolPanelHeader">
        <span className="viewerToolPanelLabel">MEASURE</span>
        <span className="viewerToolPanelName">Pick what to measure</span>
        <div className="viewerToolUnits">
          {measureUnitOptions.map(({ units, label }) => (
            <input
              key={units}
              type="button"
              value={label}
              className={
                units === measureUnits
                  ? "viewerToolUnit viewerToolUnitSelected"
                  : "viewerToolUnit"
              }
              onClick={() => {
                setMeasureUnits(units);
              }}
            />
          ))}
        </div>
        <div className="pdfMeasureScale">
          <span>Scale of page {pageNumber}</span>
          <select
            aria-label={`Scale of page ${pageNumber}`}
            value={pageScale.label}
            onChange={(event) => {
              const scale =
                presetScales.find(
                  ({ label }) => label === event.target.value,
                ) ?? PDF_FULL_SIZE_SCALE;
              PdfHandler.SetPageScale(pageNumber, scale);
            }}
          >
            <option value={PDF_FULL_SIZE_SCALE.label}>
              {PDF_FULL_SIZE_SCALE.label}
            </option>
            {pageScale.label === "Calibrated" ? (
              <option value="Calibrated">Calibrated</option>
            ) : (
              <></>
            )}
            {PDF_SCALE_PRESETS.map(({ group, scales }) => (
              <optgroup key={group} label={group}>
                {scales.map(({ label }) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        {calibration ? (
          <div className="pdfMeasureScale">
            <span>
              How long is the line on page {calibration.pageNumber}? (
              {measureUnits === "IMPERIAL" ? `e.g. 12'-6"` : "e.g. 3.75 m"})
            </span>
            <input
              type="text"
              className="pdfSearchInput"
              autoFocus
              value={knownLength}
              onChange={(event) => {
                setKnownLength(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  ApplyKnownLength();
                } else if (event.key === "Escape") {
                  PdfHandler.CancelCalibration();
                }
              }}
            />
            <div className="pdfSearchSteps">
              <input
                type="button"
                value="Set scale"
                disabled={knownLengthMetres === null}
                onClick={ApplyKnownLength}
              />
              <input
                type="button"
                value="Cancel"
                onClick={() => {
                  PdfHandler.CancelCalibration();
                }}
              />
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="viewerToolRowWrapper">
        {measureTools.map(({ tool, label, hint }) => (
          <div
            key={tool}
            className={
              tool === measureTool
                ? "viewerToolRow viewerToolRowSelected"
                : "viewerToolRow"
            }
            onClick={() => {
              setMeasureTool(tool);
            }}
          >
            <span className="viewerToolRowLabel">{label}</span>
            <span className="viewerToolRowHint">{hint}</span>
          </div>
        ))}
        {measurements.length > 0 ? (
          <div className="pdfMeasureList">
            {measurements.map((measurement) => (
              <div key={measurement.id} className="pdfMeasureListRow">
                <span className="pdfMeasureListName">
                  {measurementNames[measurement.tool]} · p.
                  {measurement.pageNumber}
                </span>
                <span className="pdfMeasureListValue">
                  {FormatPdfMeasurement(
                    measurement,
                    pageScales[measurement.pageNumber] ?? PDF_FULL_SIZE_SCALE,
                    measureUnits,
                  )}
                </span>
                <input
                  type="button"
                  value="×"
                  title="Remove"
                  onClick={() => {
                    PdfHandler.RemoveMeasurement(measurement.id);
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="viewerToolPanelFooter">
        <span>
          Drag to move around while measuring. Esc cancels the one being drawn,
          Backspace removes its last point. Measurements and scales are yours
          only and go when the viewer closes.
        </span>
        <input
          type="button"
          value="Clear all measurements"
          onClick={() => {
            PdfHandler.ClearMeasurements();
          }}
        />
      </div>
    </div>
  );
};

export default PdfMeasureMenu;
